import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";

/**
 * The single place the app talks to Sentry.
 *
 * Everything that reports errors or identifies users goes through here so we
 * have one source of truth for what telemetry we emit, how it's tagged, and
 * whether it's enabled in the current environment.
 */

// Flip to true to emit telemetry from a local `ionic serve` dev session.
const FORCE_MONITORING = false;

// Native builds ship the production web bundle, so NODE_ENV === 'production'
// covers both native (iOS/Android) and production web. Local `ionic serve` runs
// as 'development' and stays silent unless FORCE_MONITORING is set.
const monitoringEnabled = FORCE_MONITORING || process.env.NODE_ENV === "production";

export function initMonitoring(): void {
    if (!monitoringEnabled) return;

    Sentry.init(
        {
            dsn: "https://408f6e7666a8ebe86d68aacfcc1c1d6e@o133104.ingest.us.sentry.io/4508048161767424",
            release: "app.swool.io@1.6",
            dist: "1.6",
        },
        SentryReact.init
    );
}

/**
 * Identify the current user — uuid only, no email/username, so we can correlate
 * events to a user via our DB without storing PII in Sentry.
 */
export function setMonitoringUser(user: { uuid: string }): void {
    if (!monitoringEnabled || !user?.uuid) return;
    Sentry.setUser({ id: user.uuid });
}

export function clearMonitoringUser(): void {
    if (!monitoringEnabled) return;
    Sentry.setUser(null);
}

/**
 * Report an unexpected non-API error (render crash, swallowed UI failure, …).
 */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
    if (!monitoringEnabled) return;
    try {
        Sentry.captureException(error, context ? { contexts: { detail: context } } : undefined);
    } catch {
        // Telemetry must never break the caller.
    }
}

const headerPresent = (headers: any, name: string): boolean => {
    if (!headers) return false;
    // axios v1 uses an AxiosHeaders instance (has .get); fall back to plain object.
    if (typeof headers.get === "function") {
        return !!headers.get(name);
    }
    return !!(headers[name] ?? headers[name.toLowerCase()]);
};

/**
 * Report an API failure, filtered to infra/unexpected cases only:
 * no-response/network errors, HTTP 401, and 5xx. Normal app-flow 4xx
 * (404 / 403 / 422 validation) are intentionally skipped to keep signal high.
 *
 * Captures the fields that distinguish failure modes — notably `hasResponse`
 * (network vs HTTP) and `hadAuthHeader` (auth race) — so intermittent failures
 * like the cold-start "Failed to retrieve …" toasts can be root-caused.
 */
export function reportApiFailure(error: any): void {
    if (!monitoringEnabled) return;
    try {
        // Our own rejected strings (e.g. companies.service) carry no useful shape.
        if (!error || typeof error === "string") return;

        const hasResponse = !!error.response;
        const httpStatus: number | null = error.response?.status ?? null;

        const interesting =
            !hasResponse ||
            httpStatus === 401 ||
            (typeof httpStatus === "number" && httpStatus >= 500);
        if (!interesting) return;

        const method = (error.config?.method ?? "").toUpperCase();
        const url = error.config?.url ?? "unknown";
        const hadAuthHeader = headerPresent(error.config?.headers, "Authorization");

        const data = {
            url,
            method,
            hasResponse,
            httpStatus,
            axiosCode: error.code ?? null, // ERR_NETWORK, ECONNABORTED, …
            hadAuthHeader,
            onLine: typeof navigator !== "undefined" ? navigator.onLine : null,
            // ms since this JS context started — cold-start failures cluster low.
            sinceLoadMs:
                typeof performance !== "undefined" ? Math.round(performance.now()) : null,
            message: error.message ?? null,
        };

        Sentry.addBreadcrumb({
            category: "api-failure",
            level: "warning",
            message: `${method} ${url} failed`,
            data,
        });

        Sentry.captureMessage(`[api-failure] ${method} ${url}`, {
            level: "warning",
            tags: {
                monitoring: "api-failure",
                api_has_response: String(hasResponse),
                api_http_status: String(httpStatus),
                api_had_auth_header: String(hadAuthHeader),
                api_axios_code: data.axiosCode ?? "none",
            },
            contexts: { apiFailure: data },
        });
    } catch {
        // Telemetry must never break the real error path.
    }
}
